import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getDatabase, ref as dbRef, push } from 'firebase/database';
import { FileAttachment } from '../types/attachments';
import { formatDateForStorage } from './DateUtils';
import { recordActivity } from '../api/activityApi';

export interface LeaseContractUploadResult {
    success: boolean;
    attachment?: FileAttachment;
    error?: string;
}

/**
 * Upload a contract file for a lease. Storage path mirrors the DB path
 * (`leases/{leaseKey}/contracts/{attachmentKey}`). Writes the Storage object
 * only; the caller stores the returned attachment via `leasesApi.addContract`.
 */
export const uploadLeaseContract = async (
    leaseKey: string,
    file: File,
    uploadedBy: string
): Promise<LeaseContractUploadResult> => {
    try {
        const db = getDatabase();
        const storage = getStorage();

        const newAttachmentRef = push(dbRef(db, `leases/${leaseKey}/contracts`));
        const attachmentKey = newAttachmentRef.key!;
        const storagePath = `leases/${leaseKey}/contracts/${attachmentKey}`;

        await uploadBytes(storageRef(storage, storagePath), file);
        recordActivity('document.uploaded', { targetKey: attachmentKey });

        const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';
        const attachment: FileAttachment = {
            key: attachmentKey,
            fileName: file.name,
            fileType: fileExtension,
            mimeType: file.type,
            storagePath,
            fileSize: file.size,
            uploadedBy,
            uploadedAt: formatDateForStorage(new Date()),
            attachmentType: 'file',
        };
        return { success: true, attachment };
    } catch (error) {
        console.error('Error uploading lease contract:', error);
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Ett fel uppstod vid uppladdning',
        };
    }
};

export const getContractDownloadUrl = async (storagePath: string): Promise<string> => {
    const storage = getStorage();
    return getDownloadURL(storageRef(storage, storagePath));
};
